import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight, Star, VideoConference } from "@phosphor-icons/react";
import { Reveal, Overline } from "@/components/Reveal";
import { scrollToId } from "@/lib/lenis";

const WORKSHOPS = [
  {
    id: "manager-development",
    title: "Manager Development",
    featured: true,
    tagline: "For organizations that need to prepare new managers, strengthen existing leaders, and improve day-to-day management.",
    offerings: [
      "Manager 101: Foundations of Leadership",
      "Coaching and Accountability",
      "Performance Conversations",
      "Employee Relations for Managers",
    ],
    lead: "Michelle, SHRM-CP",
  },
  {
    id: "skills-labs",
    title: "Workplace Skills Labs",
    featured: true,
    tagline: "Practical workshops focused on skills employees and managers can use immediately.",
    offerings: [
      "Say It Better: 5 Communication Habits That Prevent Workplace Drama",
      "Professional Communication",
      "Difficult Conversations",
      "Conflict Resolution",
      "Collaboration and Workplace Accountability",
    ],
    lead: "Michelle & Brandi",
  },
];

const CONSULTING = [
  {
    id: "hr-consulting",
    title: "HR Consulting",
    featured: false,
    tagline: "Practical HR guidance for businesses that need help navigating employee relations, performance management, workplace concerns, and HR processes.",
    offerings: [],
    lead: "Michelle, SHRM-CP",
  },
  {
    id: "ops-consulting",
    title: "Operations Consulting",
    featured: false,
    tagline: "Support for organizations looking to improve workflows, clarify responsibilities, strengthen accountability, and create more consistent day-to-day operations.",
    offerings: [],
    lead: "Brandi",
  },
];

const ServiceAccordion = ({ items, startIndex = 0 }) => {
  const [openId, setOpenId] = useState(items[0]?.id ?? null);

  return (
    <div className="border-t border-ink/10">
      {items.map((s, i) => {
        const open = openId === s.id;
        return (
          <Reveal key={s.id} delay={i * 0.05}>
            <div className="border-b border-ink/10">
              <button
                data-testid={`service-row-${s.id}`}
                onClick={() => setOpenId(open ? null : s.id)}
                aria-expanded={open}
                className="w-full py-7 md:py-9 flex items-center gap-5 md:gap-10 text-left group"
              >
                <span className="font-serif italic text-terracotta text-lg md:text-xl w-10 shrink-0">
                  {String(startIndex + i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1">
                  <span className="flex flex-wrap items-center gap-3">
                    <span
                      className={`block font-display font-bold tracking-tight text-2xl md:text-4xl transition-colors duration-300 ${
                        open ? "text-terracotta" : "text-ink group-hover:text-terracotta"
                      }`}
                    >
                      {s.title}
                    </span>
                    {s.featured && (
                      <span
                        data-testid={`service-featured-${s.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/10 text-terracotta text-[11px] md:text-xs font-bold tracking-[0.15em] uppercase px-3.5 py-1.5"
                      >
                        <Star size={13} weight="fill" />
                        Featured
                      </span>
                    )}
                  </span>
                  <span className="block mt-1 text-sm md:text-base text-ink-soft max-w-2xl">{s.tagline}</span>
                </span>
                <motion.span
                  animate={{ rotate: open ? 45 : 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className={`shrink-0 w-11 h-11 md:w-14 md:h-14 rounded-full border flex items-center justify-center transition-colors duration-300 ${
                    open ? "bg-terracotta border-terracotta text-white" : "border-ink/20 text-ink group-hover:border-terracotta group-hover:text-terracotta"
                  }`}
                >
                  <Plus size={22} weight="bold" />
                </motion.span>
              </button>

              <AnimatePresence initial={false}>
                {open && (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className={`pb-10 md:pb-14 pl-0 md:pl-20 grid grid-cols-1 gap-8 md:gap-12 ${s.offerings.length ? "md:grid-cols-12" : ""}`}>
                      <div className={s.offerings.length ? "md:col-span-6" : "max-w-2xl"}>
                        <p className="text-base md:text-lg leading-relaxed text-ink-soft">{s.tagline}</p>
                        <p className="mt-5 text-sm font-semibold text-ink-soft flex items-center gap-2">
                          <VideoConference size={16} weight="duotone" className="text-terracotta" />
                          Offered virtually or in person
                        </p>
                        <p className="mt-4 text-xs tracking-[0.2em] uppercase font-bold text-terracotta">
                          Led by {s.lead}
                        </p>
                        <button
                          data-testid={`service-cta-${s.id}`}
                          onClick={() => scrollToId("#contact")}
                          className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold px-6 py-3 hover:bg-terracotta hover:scale-105 transition-[background-color,transform] duration-300"
                        >
                          Schedule a Consultation
                          <ArrowRight size={16} weight="bold" />
                        </button>
                      </div>
                      {s.offerings.length > 0 && (
                        <div className="md:col-span-6">
                          <div className="bg-cream rounded-2xl p-6 md:p-8">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-terracotta mb-5">
                              What's included
                            </p>
                            <ul className="space-y-3.5">
                              {s.offerings.map((o) => (
                                <li key={o} className="text-sm md:text-base leading-relaxed text-ink-soft flex gap-2.5">
                                  <span className="text-terracotta mt-1">•</span>{o}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
};

const SectionHeader = ({ overline, children, note }) => (
  <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
    <Reveal className="md:col-span-4">
      <Overline>{overline}</Overline>
    </Reveal>
    <Reveal delay={0.1} className="md:col-span-8">
      <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-tight text-ink max-w-2xl">
        {children}
      </h2>
      {note && (
        <p className="mt-5 text-base md:text-lg leading-relaxed text-ink-soft max-w-xl">{note}</p>
      )}
    </Reveal>
  </div>
);

export const Workshops = () => (
  <section id="workshops" data-testid="workshops-section" className="py-24 md:py-32 px-6 md:px-10">
    <div className="max-w-7xl mx-auto">
      <SectionHeader
        overline="Workshops & Training"
        note="Interactive development experiences your managers and teams can put to work immediately. Every session is available virtually or in person."
      >
        Skills your people can use{" "}
        <span className="font-serif italic font-medium text-terracotta">Monday morning.</span>
      </SectionHeader>
      <ServiceAccordion items={WORKSHOPS} startIndex={0} />
    </div>
  </section>
);

export const Consulting = () => (
  <section id="consulting" data-testid="consulting-section" className="bg-cream py-24 md:py-32 px-6 md:px-10">
    <div className="max-w-7xl mx-auto">
      <SectionHeader
        overline="Consulting Services"
        note="Every engagement is led directly by our Principal Partners, Michelle and Brandi. You get senior leadership on your business, never a junior handoff."
      >
        Senior HR &amp; operations guidance,{" "}
        <span className="font-serif italic font-medium text-terracotta">on tap.</span>
      </SectionHeader>
      <ServiceAccordion items={CONSULTING} startIndex={2} />
    </div>
  </section>
);
